import { Component, OnInit } from '@angular/core';
import { ContactService } from './services/contact.service';
import { map, Observable, of } from 'rxjs';
import { ContactPerson } from './models/contact';
import { AsyncPipe } from '@angular/common';
import { ContactItemComponent } from './components/contact-item/contact-item.component';
import { DepartmentItemComponent } from './components/department-item/department-item.component';
import { GeneralInfoComponent } from './components/general-info/general-info.component';
import { ContactItemSkeletonComponent } from './components/contact-item-skeleton/contact-item-skeleton.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';

interface ContactSections {
  people: ContactPerson[];
  departments: ContactPerson[];
}

@Component({
  selector: 'app-contact',
  imports: [
    AsyncPipe,
    ContactItemComponent,
    DepartmentItemComponent,
    GeneralInfoComponent,
    ContactItemSkeletonComponent,
    PageHeaderComponent
  ],
  standalone: true,
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss'
})
export class ContactComponent implements OnInit {
  contactSections$: Observable<ContactSections> = of({ people: [], departments: [] });

  readonly numberOfSkeletons = new Array(6);
  readonly defaultContactInfo: ContactPerson = {
    id: '1',
    type: 'Person',
    title: null,
    sortOrder: 1,
    name: 'Support',
    role: null,
    photoUrl: null,
    email: 'headoffice@scangl.com',
    businessPhone: '+45 32 48 00 00',
    phone: null,
    address: 'Jernholmen 49, 2650 Hvidovre, Denmark',
    jobTitle: 'Global Headquarters',
    description: null
  };

  constructor(private contactService: ContactService) {}

  ngOnInit() {
    this.contactSections$ = this.contactService.getContacts().pipe(
      map(contacts => ({
        people: this.uniqueById(
          contacts.flatMap(contact => contact.contacts.filter(person => (person.type ?? 'Person') === 'Person'))
        ),
        departments: this.uniqueById(
          contacts.flatMap(contact => contact.contacts.filter(person => person.type === 'Department'))
        )
      }))
    );
  }

  // The same person/department can be assigned to multiple parties — flattening across parties
  // (see ngOnInit above) would otherwise render one card per assignment instead of per contact.
  // Each party can also independently show/hide that contact's mobile phone and photo, so the
  // occurrences are grouped by id and resolved together rather than just deduped.
  private uniqueById(contacts: ContactPerson[]): ContactPerson[] {
    const occurrencesById = new Map<string, ContactPerson[]>();
    for (const contact of contacts) {
      occurrencesById.set(contact.id, [...(occurrencesById.get(contact.id) ?? []), contact]);
    }
    return Array.from(occurrencesById.values()).map(occurrences => this.applyOrgVisibility(occurrences));
  }

  // A contact's org-level flag only restricts visibility for that specific party — if any party
  // the contact is assigned to allows it (true, or unset/null), show it.
  private applyOrgVisibility(occurrences: ContactPerson[]): ContactPerson {
    const [first] = occurrences;
    const showMobilePhone = occurrences.some(contact => contact.orgShowMobilePhone !== false);
    const showPhoto = occurrences.some(contact => contact.orgShowPhoto !== false);
    return {
      ...first,
      phone: showMobilePhone ? first.phone : null,
      photoUrl: showPhoto ? first.photoUrl : null
    };
  }
}
